import { motion } from 'framer-motion';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'light' | 'dark' | 'copper';
}

export default function Logo({ size = 'md', variant = 'light' }: LogoProps) {
  const sizeClasses = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-14 h-14 text-lg',
    lg: 'w-20 h-20 text-2xl',
  };

  const colorClasses = {
    light: {
      ring: 'border-cream/30',
      inner: 'border-cream/15',
      text: 'text-cream',
      ampersand: 'text-copper-light',
      ornament: 'text-cream/20',
    },
    dark: {
      ring: 'border-midnight/30',
      inner: 'border-midnight/15',
      text: 'text-midnight',
      ampersand: 'text-copper',
      ornament: 'text-midnight/15',
    },
    copper: {
      ring: 'border-copper/50',
      inner: 'border-copper/20',
      text: 'text-copper',
      ampersand: 'text-copper-light',
      ornament: 'text-copper/30',
    },
  };

  const colors = colorClasses[variant];

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className={`relative ${sizeClasses[size]} flex items-center justify-center`}
    >
      {/* Outer decorative ring */}
      <div className={`absolute inset-0 rounded-full border ${colors.ring}`} />

      {/* Inner ring */}
      <div className={`absolute inset-1.5 rounded-full border ${colors.inner}`} />

      {/* Decorative corner flourishes */}
      <span className={`absolute -top-0.5 left-1/2 -translate-x-1/2 ${colors.ornament}`}>
        <svg width="8" height="6" viewBox="0 0 8 6" fill="currentColor">
          <path d="M4 0C4 0 0 3 0 3C0 3 4 6 4 6C4 6 8 3 8 3C8 3 4 0 4 0Z" />
        </svg>
      </span>
      <span className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 ${colors.ornament}`}>
        <svg width="8" height="6" viewBox="0 0 8 6" fill="currentColor">
          <path d="M4 6C4 6 0 3 0 3C0 3 4 0 4 0C4 0 8 3 8 3C8 3 4 6 4 6Z" />
        </svg>
      </span>
      <span className={`absolute top-1/2 -left-0.5 -translate-y-1/2 ${colors.ornament}`}>
        <svg width="6" height="8" viewBox="0 0 6 8" fill="currentColor">
          <path d="M0 4C0 4 3 0 3 0C3 0 6 4 6 4C6 4 3 8 3 8C3 8 0 4 0 4Z" />
        </svg>
      </span>
      <span className={`absolute top-1/2 -right-0.5 -translate-y-1/2 ${colors.ornament}`}>
        <svg width="6" height="8" viewBox="0 0 6 8" fill="currentColor">
          <path d="M6 4C6 4 3 0 3 0C3 0 0 4 0 4C0 4 3 8 3 8C3 8 6 4 6 4Z" />
        </svg>
      </span>

      {/* Monogram text */}
      <span className={`font-calligraphic ${colors.text} relative z-10 leading-none tracking-tight`}>
        S
        <span className={`font-serif text-[0.6em] mx-[0.1em] ${colors.ampersand}`}>&</span>
        A
      </span>
    </motion.div>
  );
}